/**
 * useAzureDiscovery — identity-safe TanStack Query wrapper around
 * `window.electronAPI.azureDiscovery` (Option C, C4a).
 *
 * Two queries:
 *  1. `['azure-discovery-identity', source]` → `resolveIdentity`: re-run on every
 *     open (`refetchOnMount: 'always'`), it returns the identity, main's
 *     `identityGeneration` and the principal derived silently.
 *  2. `['azure-discovery', source, account, tenantId, identityGeneration, scopeKey]`
 *     → `list`, rows cached for the session (an error result is stale at once, so
 *     reopening retries). The generation is
 *     part of the key, so an identity change re-keys the rows: the selection is
 *     pruned synchronously (the resource set is replaced) and a stale reply is
 *     never shown. Main also refuses a list whose generation moved.
 *
 * Cancellation: the query's `signal` is consumed, so a picker close (unmount),
 * Refresh (`cancelRefetch`) or a key change aborts the fetch and sends
 * `azure-discovery:cancel { requestId }` to main.
 *
 * Revalidation: rows are usable only while the current principal equals their
 * owner (`samePrincipal`). A mismatch (`ownerMismatch`), an identity resolve in
 * flight (`verifying`) or a failed silent derivation (its classified error is
 * exposed) keeps confirm disabled; a mismatch also starts a refetch. Every
 * confirmation re-resolves first (`confirmNow`). An `identity-changed` reply
 * re-resolves the identity, which moves the key.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AzureDiscoveryError,
  AzureDiscoveryListResult,
  AzureDiscoveryPrincipal,
  AzureDiscoveryResolveIdentityResult,
  AzureIdentityInfo,
  AzureIdentitySource,
} from '../../../types/azureDiscovery';
import { samePrincipal } from '../../../types/azureDiscovery';

const UNAVAILABLE: AzureDiscoveryError = { code: 'unknown', message: 'Azure discovery is unavailable in this build.' };

/** The subscription scope key. `'all'` until C4b adds a selection. */
export const SCOPE_KEY = 'all';

export function identityQueryKey(source: AzureIdentitySource | undefined) {
  return ['azure-discovery-identity', source] as const;
}

export function discoveryQueryKey(
  source: AzureIdentitySource | undefined,
  identity: { identity: AzureIdentityInfo; identityGeneration: number } | undefined
) {
  if (!identity) return ['azure-discovery', 'pending'] as const;
  return [
    'azure-discovery',
    source ?? identity.identity.source,
    identity.identity.account ?? '',
    identity.identity.tenantId ?? '',
    identity.identityGeneration,
    SCOPE_KEY,
  ] as const;
}

function newRequestId(): string {
  const c = globalThis.crypto as { randomUUID?: () => string } | undefined;
  return c?.randomUUID ? c.randomUUID() : `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function useAzureDiscovery(params: { open: boolean; source?: AzureIdentitySource }) {
  const { open, source } = params;
  const interactiveRef = useRef(false);
  const queryClient = useQueryClient();

  const identityQuery = useQuery<AzureDiscoveryResolveIdentityResult>({
    queryKey: identityQueryKey(source),
    queryFn: async () => {
      const api = window.electronAPI?.azureDiscovery;
      if (!api) return { ok: false, error: UNAVAILABLE };
      return api.resolveIdentity({ source });
    },
    enabled: open,
    staleTime: 0,
    refetchOnMount: 'always',
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const resolved = identityQuery.data?.ok ? identityQuery.data : undefined;
  const identityError = identityQuery.data && !identityQuery.data.ok ? identityQuery.data.error : undefined;

  const listKey = discoveryQueryKey(source, resolved);
  const listQuery = useQuery<AzureDiscoveryListResult>({
    queryKey: listKey,
    queryFn: async ({ signal }) => {
      const api = window.electronAPI?.azureDiscovery;
      if (!api || !resolved) return { ok: false, error: UNAVAILABLE };
      const requestId = newRequestId();
      const cancel = () => void api.cancel({ requestId }).catch(() => undefined);
      signal.addEventListener('abort', cancel, { once: true });
      try {
        const result = await api.list({
          source,
          interactive: interactiveRef.current,
          identityGeneration: resolved.identityGeneration,
          scope: { subscriptions: SCOPE_KEY },
          requestId,
        });
        interactiveRef.current = false;
        return result;
      } finally {
        signal.removeEventListener('abort', cancel);
      }
    },
    enabled: open && resolved !== undefined,
    // Rows are cached for the session; an error is never — reopening the picker retries it.
    staleTime: (query) => (query.state.data?.ok ? Infinity : 0),
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // A reply from a moved generation (a sign-in inside the list, or main refusing the stale generation) re-resolves the
  // identity, which re-keys the rows. Once per reply object.
  const handledReply = useRef<AzureDiscoveryListResult | undefined>(undefined);
  useEffect(() => {
    const reply = listQuery.data;
    if (!reply || handledReply.current === reply || !resolved) return;
    handledReply.current = reply;
    const moved = reply.ok ? reply.identityGeneration !== resolved.identityGeneration : reply.error.code === 'identity-changed';
    if (!moved) return;
    if (reply.ok) queryClient.setQueryData(discoveryQueryKey(source, { identity: reply.identity, identityGeneration: reply.identityGeneration }), reply);
    void identityQuery.refetch();
  }, [listQuery.data, resolved, source, queryClient, identityQuery]);

  const rows = listQuery.data?.ok ? listQuery.data : undefined;
  const principal: AzureDiscoveryPrincipal | undefined = resolved?.principal;
  // "Verifying" only while work is in flight; a failed silent derivation is an explicit, classified error (C4A-04).
  const verifying = identityQuery.isFetching;
  const revalidationError = rows !== undefined && principal === undefined ? resolved?.principalError : undefined;
  const ownerMismatch = rows !== undefined && principal !== undefined && !samePrincipal(rows.owner, principal);

  // Every confirmation owns a token (r2 issue 1): a Refresh, another confirmation, an identity change, a dismissal or
  // the unmount moves it, and a check that comes back to a moved token is refused — cached identity data (what a
  // cancelled query hands back) is never proof of the current identity; only THIS check's fresh resolution is.
  const confirmToken = useRef(0);
  const invalidateConfirmations = useCallback(() => {
    confirmToken.current += 1;
  }, []);
  useEffect(() => () => invalidateConfirmations(), [invalidateConfirmations]);
  useEffect(() => {
    invalidateConfirmations();
  }, [source, resolved?.identityGeneration, invalidateConfirmations]);

  /**
   * Before a confirmation (C4A-01): resolve the current principal AFRESH (straight from main, not the query cache) and
   * compare it with the rows' owner and generation. `false` refuses the pick; the fresh identity is written into the
   * identity query so a moved generation re-keys the rows and a same-generation mismatch refetches them.
   */
  const confirmNow = useCallback(async (): Promise<boolean> => {
    const current = listQuery.data?.ok ? listQuery.data : undefined;
    const api = window.electronAPI?.azureDiscovery;
    if (!current || !api) return false;
    const token = ++confirmToken.current;
    let fresh: AzureDiscoveryResolveIdentityResult;
    try {
      fresh = await api.resolveIdentity({ source });
    } catch {
      return false;
    }
    if (token !== confirmToken.current) return false; // superseded, dismissed or unmounted meanwhile
    queryClient.setQueryData(identityQueryKey(source), fresh);
    if (!fresh.ok || !fresh.principal) return false;
    return fresh.identityGeneration === current.identityGeneration && samePrincipal(current.owner, fresh.principal);
  }, [listQuery.data, source, queryClient]);

  // Rows owned by another principal: never confirmable; a refetch of the current identity's rows starts (once per resolve).
  const refetchedFor = useRef<AzureDiscoveryResolveIdentityResult | undefined>(undefined);
  useEffect(() => {
    if (!ownerMismatch || refetchedFor.current === identityQuery.data) return;
    refetchedFor.current = identityQuery.data;
    void listQuery.refetch();
  }, [ownerMismatch, identityQuery.data, listQuery]);

  /** Set whether the *next* fetch may open the interactive Entra browser flow. */
  const setInteractive = useCallback((value: boolean) => {
    interactiveRef.current = value;
  }, []);

  /** Refresh: re-resolve the identity (a new generation re-keys the list), then fetch; an in-flight fetch is cancelled. */
  const refetch = useCallback(async () => {
    invalidateConfirmations();
    await queryClient.cancelQueries({ queryKey: ['azure-discovery'] });
    const identity = await identityQuery.refetch();
    const next = identity.data?.ok ? identity.data : undefined;
    if (next && next.identityGeneration === resolved?.identityGeneration) await listQuery.refetch();
  }, [queryClient, identityQuery, listQuery, resolved, invalidateConfirmations]);

  return {
    identity: resolved?.identity,
    identityGeneration: resolved?.identityGeneration,
    principal,
    identityError: identityError ?? revalidationError,
    data: listQuery.data,
    isLoading: identityQuery.isLoading || (resolved !== undefined && listQuery.isLoading),
    isFetching: identityQuery.isFetching || listQuery.isFetching,
    verifying,
    ownerMismatch,
    /** Rows may be confirmed: current principal known and equal to the rows' owner. */
    confirmable: rows !== undefined && principal !== undefined && !verifying && !ownerMismatch,
    confirmNow,
    invalidateConfirmations,
    refetch,
    setInteractive,
  };
}
